import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLanguage } from './LanguageContext';
import OptimizedImage from './components/OptimizedImage';
import ContactForm from './components/ContactForm';

import img1 from './assets/practice/1.webp';
import img2 from './assets/practice/2.webp';
import img3 from './assets/practice/3.webp';
import img4 from './assets/practice/4.webp';

const Practice = () => {
  const { t } = useLanguage();

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingRight: '1rem',
    paddingLeft: '1rem',
    fontFamily: 'Sora, sans-serif',
  };
  
  const contactInfoStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
    padding: '1.5rem',
    backgroundColor: '#f8f8f8',
    borderRadius: '10px',
    marginTop: '3rem',
  };

  const contentStyle = {
    width: '100%',
    maxWidth: '768px',
    textAlign: 'center',
    paddingBlock: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };

  const headingStyle1 = {
    color: '#666666',
    fontWeight: '500',
    lineHeight: '1.1',
    letterSpacing: '-1px',
    fontSize: '1.8rem',
    marginBottom: '1rem',
    textAlign: 'center'
  };

  const textStyle = {
    color: '#666666',
    lineHeight: '1.6',
    fontWeight: '300',
    fontSize: '1rem',
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    marginBottom: '2rem'
  };

  const mapContainerStyle = {
    width: '100%',
    maxWidth: '800px',
    height: '400px',
    margin: '3rem auto 2rem auto',
    borderRadius: '5px',
    overflow: 'hidden',
    filter: 'grayscale(60%)',
  };

  const mapStyle = {
    width: '100%',
    height: '100%',
    border: '0'
  };

  const signatureStyle = {
    color: '#666666',
    padding: '1rem 0 0.3rem 0',
    fontWeight: '500',
    fontSize: '1.2rem'
  };

  const carouselContainerStyle = {
    width: '100%',
    maxWidth: '768px',
    position: 'relative',
  };

  const sliderWrapperStyle = {
    '.slick-slide': {
      padding: 0,
    },
    '.slick-list': {
      overflow: 'hidden',
    },
    '.slick-track': {
      display: 'flex',
      gap: '30px',
    },
    '.slick-slide > div': {
      width: '100%',
      height: '100%',
    }
  };

  const linkStyle = {
    color: '#666666',
    transition: 'color 0.3s ease',
    '&:hover': {
      color: '#333333'
    }
  };

  const buttonStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: 2,
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-50%) scale(1.1)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    }
  };

  const PrevArrow = (props) => {
    const { onClick, currentSlide } = props;
    return (
      <button
        style={{ ...buttonStyle, left: '10px' }}
        onClick={onClick}
        disabled={currentSlide === 0}
        aria-label="Previous slide"
      >
        <i className="fas fa-chevron-left" style={{ color: '#666666' }}></i>
      </button>
    );
  };

  const NextArrow = (props) => {
    const { onClick, currentSlide, slideCount, slidesToShow } = props;
    return (
      <button
        style={{ ...buttonStyle, right: '10px' }}
        onClick={onClick}
        disabled={currentSlide >= slideCount - slidesToShow}
        aria-label="Next slide"
      >
        <i className="fas fa-chevron-right" style={{ color: '#666666' }}></i>
      </button>
    );
  };

  const imageStyle = {
    height: '400px',
    width: '100%',
    objectFit: 'cover',
    margin: '0 auto',
    borderRadius: '5px',
    display: 'block',
    '@media (max-width: 768px)': {
      height: '300px',
      width: '180px',
    },
    padding: '0 10px',
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '65px',
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 768, // mobile
        settings: {
          centerPadding: '20px',
        }
      }
    ]
  };

  const practiceImages = [img1, img2, img3, img4];

  return (
    <section id="praxis" style={containerStyle}>
      <div style={contentStyle}>
        <h1 style={headingStyle1}>{t('practice')}</h1>
        <p style={textStyle}>
          {t('practiceText')}
        </p>
        <div style={carouselContainerStyle}>
          <div style={sliderWrapperStyle}>
            <Slider {...settings}>
              {practiceImages.map((image, index) => (
                <div key={index}>
                  <OptimizedImage src={image} alt={`Praxis ${index + 1}`} style={imageStyle} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
        <div style={contactInfoStyle}>
          <p style={signatureStyle}>
            {t('theraConnectText')}
            <a 
              href="https://theraconnect.de/app?connect=KQAAAAJnd19pZAAZAAAATVRVek1URTYzTGh3MDJONDdCSkhSaWQ0AAA" 
              target="_blank" 
              rel="noopener noreferrer"
              style={linkStyle}
            >
              TheraConnect App
            </a>
            {t('theraConnectText2')}
          </p>
          <p style={signatureStyle}>
            {t('contactText')}
            <a href="tel:+498934077791" style={linkStyle}>{t('byPhone')}</a> {t('or')} <a href="mailto:info@uweveronik.de" style={linkStyle}>{t('byEmail')}</a>.
          </p>
        </div>
        <style>{`
          .practice-contact-row {
            display: flex;
            flex-direction: column;
            width: 100%;
            margin: 0 0 2rem 0;
          }
          @media (min-width: 700px) {
            .practice-contact-row {
              flex-direction: row;
              align-items: center;
              gap: 40px;
              width: 100%;
              margin: 3rem 0 2rem 0;
            }
            .practice-map {
              flex: 1 1 0;
              height: 530px;
              min-width: 0;
              max-width: none;
            }
            .practice-form {
              flex: 1 1 0;
              min-width: 0;
              max-width: none;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
            }
          }
        `}</style>
        <div className="practice-contact-row">
          <div className="practice-map" style={{...mapContainerStyle, height: '530px', maxWidth: 'none'}}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2662.123456789012!2d11.5812345!3d48.1523456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479e75f9c1234567%3A0xabcdef1234567890!2sLeopoldstr.%2018%2C%2080802%20M%C3%BCnchen!5e0!3m2!1sde!2sde!4v1234567890!5m2!1sde!2sde"
              style={{...mapStyle, height: '530px'}}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Practice Location"
            ></iframe>
          </div>
          <div className="practice-form">
            <ContactForm noBackground />
          </div>
        </div>
        <div id="kontakt" style={textStyle}>
          {t('practiceInfo').split('\n').map((line, idx) => (
            <React.Fragment key={idx}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Practice;